<template>
  <BaseTemplate :currentUser="currentUser">
    <div class="base">
      <Title :jpn="t('出金申請')"></Title>

      <!-- 出金可能額 -->
      <div class="input-area">
        <div class="label-item">
          <span class="label"> {{ t('出金可能額') }}</span>
          <span class="text">{{ availableAmount | comma }}{{ t('円') }}</span>
        </div>

        <!-- 振込金額入力 -->
        <LabelInput class="label-input" :labelText="t('振込金額')">
          <TextBox
            class="text-box"
            inputType="number"
            :value="paymentAmount"
            placeholder="0"
            @handleTextInput="inputPaymentAmount"
          />
        </LabelInput>

        <!-- 振込方法選択 -->
        <LabelInput class="label-input" :labelText="t('振込方法')">
          <SelectBox
            :memberChange="true"
            :options="paymentTypeOptions"
            :selectedOption="paymentType"
            :value="paymentType"
            @handleSelectChange="changePaymentType"
          />
        </LabelInput>
        <div class="attention-area">
          <div class="attention">
            <p v-if="isJapan">{{ t('振込額3万円以下は350円の振込手数料がかかります。') }}</p>
            <p>{{t('振込申請日の5営業日後に送金させていただきます。')}}</p>
          </div>
          <ErrorMessage
            :style="{ visibility: errorMessage ? 'visible' : 'hidden' }"
            class="error-message"
            :message="errorMessage"
          />
        </div>
        <!-- 申請ボタン -->
        <div class="button_area">
          <BuyButton
            :isArrow="false"
            class="square-button"
            :text="t('申請する')"
            @handleSquareBtnClick="submitPaymentDialog"
          />
        </div>
      </div>
      <!-- 申請履歴 -->
      <Title :jpn="t('出金申請履歴')" class="subtitle" />
      <div
        class="history-list"
        v-if="
          fetchPaymentHistories &&
            fetchPaymentHistories.list &&
            fetchPaymentHistories.list.length > 0
        "
      >
        <div class="legend">
          <span class="legend-item">
            <span class="white-indicator"></span>{{ t('未入金') }}
            <span class="blue-indicator"></span>{{ t('入金済み') }}
          </span>
        </div>
        <table>
          <thead>
            <tr>
              <th>{{ t('申請日') }}</th>
              <th>{{ t('申請振込金額') }}</th>
              <th>{{ t('振込手数料') }}</th>
              <th>{{ t('振込金額') }}</th>
              <th>{{ t('振込方法') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="history in fetchPaymentHistories.list"
              :key="history.id"
              :class="{ 'paid-row': history.status !== 0 }"
            >
              <td>{{ formatDate(history.application_date) }}</td>
              <td>{{ history.total_amount | comma }}{{ t('円') }}</td>
              <td>
                <span v-if="history.payment_type === 0"
                  >{{ history.fee | comma }}{{ t('円') }}</span
                >
                <span v-else>{{ t('Please check PayPal.') }}</span>
              </td>
              <td>
                <span v-if="history.payment_type === 0"
                  >{{ history.payment_amount | comma }}{{ t('円') }}</span
                >
                <span v-else>{{ t('Please check PayPal.') }}</span>
              </td>
              <td>{{ formatPaymentType(history.payment_type) }}</td>
            </tr>
          </tbody>
        </table>
      </div>
      <div v-else class="no-history">
        <p>{{ t('申請履歴はありません') }}</p>
      </div>
      <div class="button_area">
        <SquareButton
          class="square-button"
          :text="t('戻る')"
          @handleSquareBtnClick="goToBack"
        />
      </div>
      <!-- 以下、モーダル -->
      <PaymentConfirmModal
        v-if="showDialog"
        :mode="initialMode"
        :selectedPaymentType="paymentType"
        :selectedPaymentInfo="selectedPaymentInfo"
        :paymentAmount="paymentAmount"
        :availableAmount="availableAmount"
        :paymentSaveErrorMessage="paymentSaveErrorMessage"
        :paymentErrorMessage="paymentErrorMessage"
        @delete="deleteAccount"
        @submit="submitPayment"
        @close="closeDialog"
        @save="submitAccountInfo"
      />
    </div>
  </BaseTemplate>
</template>

<script>
import { mapGetters } from 'vuex';
import BaseTemplate from '@/components/templates/BaseTemplate.vue';
import Title from '@/components/molecules/Title02.vue';
import LabelInput from '@/components/molecules/LabelInput.vue';
import TextBox from '@/components/atoms/TextBox.vue';
import SelectBox from '@/components/atoms/SelectBox.vue';
import BuyButton from '@/components/atoms/BuyButton.vue';
import SquareButton from '@/components/atoms/SquareButton.vue';
import t from '@/plugins/i18n';
import { separateCharacterNumber } from '@/common/util/format';
import PaymentConfirmModal from '@/components/organisms/PaymentConfirmModal.vue';
import ErrorMessage from '@/components/atoms/ErrorMessage.vue';

export default {
  mixins: [t],
  components: {
    BaseTemplate,
    Title,
    LabelInput,
    TextBox,
    SelectBox,
    BuyButton,
    SquareButton,
    ErrorMessage,
    PaymentConfirmModal,
  },
  data() {
    return {
      initialMode: 'register',
      paymentAmount: null,
      paymentType: 0,
      paymentHistories: [],
      paymentTypeOptions: [],
      showDialog: false,
      showAccountDialog: false,
      paymentSaveErrorMessage: {},
      paymentErrorMessage: {},
      selectedPaymentInfo: null,
      errorMessage: '',
    };
  },

  filters: {
    comma(value) {
      return separateCharacterNumber(value);
    },
  },
  watch: {
    // 振込先登録した後取得しなおしてる
    paymentSave(newResult) {
      if (
        newResult.state !== 200 || (newResult.error && newResult.error.length !== 0)
      ) {
        this.paymentSaveErrorMessage = newResult.errors; // ← オブジェクトを渡す
        return;
      }
      this.$store.dispatch('market/payment_list');
      this.initialMode = 'confirm';
      this.paymentSaveErrorMessage = '';
    },
    // 振込先リスト更新時に選択された振込先情報を更新
    paymentList(newList) {
      // 新しく保存された振込先情報がある場合、選択された振込先情報を更新
      if (newList && newList.list && this.initialMode === 'confirm') {
        const paymentListArray = Object.values(newList.list);
        const selectedPayment = paymentListArray.find(
          (p) => Number(p.payment_type) === Number(this.paymentType),
        );
        if (selectedPayment) {
          this.selectedPaymentInfo = selectedPayment;
        }
      }
    },
    // 振込申請
    payment(newResult) {
      if (
        newResult.state !== 200 || (newResult.errors && newResult.errors.length !== 0)
      ) {
        this.paymentErrorMessage = newResult.errors; // ← オブジェクトを渡す
        return;
      }
      this.$store.dispatch('market/payment_history');
      this.$store.dispatch('user/setCurrentUser');
      this.closeDialog();
    },
    // 振込先削除
    paymentDelete(newResult) {
      if (
        newResult.state !== 200 || (newResult.errors && newResult.errors.length !== 0)
      ) {
        return;
      }
      this.$store.dispatch('market/payment_list');
      this.initialMode = 'register';
    },
  },

  computed: {
    ...mapGetters({
      currentUser: 'user/currentUser',
      requestPayment: 'market/resultMarketPayment',
      fetchPaymentHistories: 'market/resultMarketPaymentHistory',
      payment: 'market/resultMarketPayment',
      paymentList: 'market/resultMarketPaymentList',
      paymentSave: 'market/resultMarketPaymentSave',
      paymentDelete: 'market/resultMarketPaymentDelete',
    }),
    availableAmount() {
      if (this.currentUser) {
        return this.currentUser.market_sales_amount;
      }
      return 0;
    },
    // 日本国内かどうかを判定
    isJapan() {
      return (
        this.currentUser && (this.currentUser.country_id === '84' || this.currentUser.country_id === 84));
    },
  },

  created() {
    // 国に応じて支払い方法を制限
    if (this.isJapan) {
      // 日本の場合は銀行振込のみ
      this.paymentTypeOptions = [{ id: 0, name: this.t('銀行振込') }];
      this.paymentType = 0; // 銀行振込を自動選択
    } else {
      // 日本以外の場合はPayPalのみ
      this.paymentTypeOptions = [{ id: 1, name: this.t('PayPal') }];
      this.paymentType = 1; // PayPalを自動選択
    }
    this.$store.dispatch('market/payment_list');
    this.$store.dispatch('market/payment_history');
  },

  methods: {
    inputPaymentAmount(val) {
      this.paymentAmount = Number(val);
      // 入力時にエラーメッセージをクリア
      this.errorMessage = '';
    },
    changePaymentType(val) {
      this.paymentType = val;
    },
    submitPaymentDialog() {
      // 振込金額のバリデーション
      if (this.isJapan && (!this.paymentAmount || this.paymentAmount < 351)) {
        this.errorMessage = this.t('振込金額は351円以上で入力してください。');
        return;
      }
      if (!this.isJapan && (!this.paymentAmount || this.paymentAmount < 10000)) {
        this.errorMessage = this.t('振込金額は10,000円以上で入力してください。');
        return;
      }
      // 出金可能額を超えていないかチェック
      if (this.paymentAmount > this.availableAmount) {
        this.errorMessage = this.t('振込金額が出金可能額を超えています。');
        return;
      }

      // エラーメッセージをクリア
      this.errorMessage = '';

      if (!this.paymentList.list) {
        this.initialMode = 'register';
        this.selectedPaymentInfo = null;
      } else {
        const paymentListArray = Object.values(this.paymentList.list);
        const selectedPayment = paymentListArray.find(
          (p) => Number(p.payment_type) === Number(this.paymentType),
        );
        this.initialMode = selectedPayment ? 'confirm' : 'register';
        this.selectedPaymentInfo = selectedPayment || null;
      }
      this.paymentErrorMessage = '';
      this.paymentSaveErrorMessage = '';
      this.showDialog = true;
    },
    // 申請の登録↓
    submitPayment(postData) {
      this.$store.dispatch('market/payment', { postParams: postData });
    },
    // 振込先の登録↓
    submitAccountInfo(accountData) {
      this.$store.dispatch('market/payment_save', { postParams: accountData });
    },
    closeDialog() {
      this.showDialog = false;
    },
    formatPaymentType(type) {
      return type === 0 ? this.t('銀行振込') : this.t('PayPal');
    },
    formatStatus(status) {
      switch (status) {
        case 0:
          return this.t('申請中');
        case 1:
          return this.t('振込完了');
        case 8:
          return this.t('キャンセル');
        default:
          return this.t('不明');
      }
    },
    goToBack() {
      this.$router.push('/');
    },
    deleteAccount(id) {
      const postParams = { id };
      this.$store.dispatch('market/payment_delete', { postParams });
      this.mode = 'register';
    },
    formatDate(dateString) {
      if (!dateString) return '';
      return dateString.split(' ')[0].replace(/-/g, '/').slice(2);
    },
  },
};
</script>

<style lang="scss" scoped>
.base {
  padding-top: 30px;
  padding-bottom: 50px;
  max-width: 600px;
  margin: auto;
  .input-area {
    margin: 0;
    padding: 0 10px;
    .label-item {
      display: flex;
      flex-direction: column;
      text-align: left;
      .label {
        font-size: 14px;
        line-height: 23.4px;
        color: $black;
      }
      .text {
        display: flex;
        justify-content: center;
        font-size: 28px;
        font-weight: bold;
        line-height: 1.8;
        padding: 0 10px;
        min-height: 40px;
      }
    }
  }
  .attention-area {
    p {
      margin-bottom: 4px;
    }
    .attention {
      padding-top: 20px;
      line-height: 0.9;
    }
    .error-message {
      text-align: center;
      height: 15px;
      line-height: 0.9;
    }
    @media screen and (max-width: $mobileWidth) {
      font-size: 11px;
    }
  }
  .no-history {
    margin: 20px 0 50px 0;
    text-align: center;
  }
  .button_area {
    margin: 20px auto;
    display: flex;
    justify-content: center;
  }
  .square-button {
    width: 200px;
    height: 40px;
    @media screen and (max-width: $mobileWidth) {
      font-size: 14px;
    }
  }
  .history-list {
    margin-top: 30px;
    @media screen and (max-width: $mobileWidth) {
      font-size: 11px;
    }
    .legend {
      margin-bottom: 10px;
      text-align: right;
      .legend-item {
        display: inline-flex;
        align-items: center;
        font-size: 14px;
        color: $gray2;
        .white-indicator {
          display: inline-block;
          width: 12px;
          height: 12px;
          background-color: #fff;
          margin-right: 5px;
          border: 1px solid #9e9e9e;
        }
        .blue-indicator {
          margin-left: 10px;
          display: inline-block;
          width: 12px;
          height: 12px;
          background-color: #e6f3ff;
          margin-right: 5px;
          border: 1px solid #b3d9ff;
        }
      }
    }
    table {
      width: 100%;
      border-collapse: collapse;
      th,
      td {
        border: 1px solid #ccc;
        padding: 8px;
        text-align: center;
        @media screen and (max-width: $mobileWidth) {
          padding: 4px;
        }
      }
      th {
        background-color: #eeeeee;
      }
      .paid-row {
        background-color: #e6f3ff;
      }
    }
  }
}
// モーダルウィンドウ______________________
.overlay_conf {
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
  .payment-modal {
    border: none;
    box-shadow: 0 0px 20px rgba(0, 0, 0, 1);
    border-radius: 10px;
    width: 600px;
    max-height: 90%;
    overflow-y: auto;
    padding: 20px;
    position: relative;
    @media screen and (max-width: $tabletWidth) {
      width: 400px;
    }
    @media screen and (max-width: $mobileWidth) {
      width: 320px;
      margin: 0 auto;
    }
    .modal-body {
      margin: 0 auto;
      width: 95%;
      margin: 10px auto;
      .payment-summary {
        padding: 10px 0;
        border-bottom: 1px solid #ddd;

        .summary-row {
          display: flex;
          justify-content: space-between;
          font-weight: bold;
          font-size: 20px;
          margin-bottom: 10px;
        }
        .detail-row {
          font-size: 14px;
          color: $gray2;
          .detail-item {
            display: flex;
            justify-content: space-between;
          }
        }
      }
    }

    .button-area {
      display: flex;
      justify-content: space-around;
      margin-top: 20px;
      gap: 10px;
      padding-bottom: 10px;
      button {
        padding: 10px 60px;
        border: none;
        cursor: pointer;
        border-radius: 0;
        font-size: 16px;
        font-weight: bold;
        box-shadow: 0 0 5px rgba(0, 0, 0, 0.4);
        @media screen and (max-width: $tabletWidth) {
          padding: 10px 35px;
        }
      }
      .square-button {
        width: 50%;
        &.disabled {
          background: #ccc;
          color: #777;
          pointer-events: none;
          cursor: not-allowed;
        }
      }
      .cancel {
        width: 50%;
        height: 40px;
        color: #a1a2a2;
      }
    }
  }
}
</style>
